import type { ReactNode } from 'react';
import { Star } from 'lucide-react';
import { Avatar, type PillTone } from '../../components';
import { isDay, type DayRange } from '../../lib/format/dates';
import type { BlueStarStatus, ChefStatus, ProfileStatus } from './api';

/**
 * Shared Chefs/Eaters furniture: BE enum → label/tone maps, the name cell,
 * the dialog header, and readers for filters held in the URL. The router
 * JSON-parses search values, so a number may arrive as a number or a string.
 */

// --- labels & tones -----------------------------------------------------------

/** Kit labels for BE `blueStarStatus` (Not started / Pending / Certified, plus Declined). */
export const BLUE_STAR_LABEL: Record<BlueStarStatus, string> = {
  none: 'Not started',
  pending: 'Pending',
  approved: 'Certified',
  declined: 'Declined',
};
export const BLUE_STAR_TONE: Record<BlueStarStatus, PillTone> = {
  none: 'neutral',
  pending: 'warning',
  approved: 'info',
  declined: 'danger',
};

export const CHEF_STATUSES: ChefStatus[] = ['active', 'inactive', 'gone'];
export const CHEF_STATUS_LABEL: Record<ChefStatus, string> = { active: 'Active', inactive: 'Inactive', gone: 'Gone' };
export const CHEF_STATUS_TONE: Record<ChefStatus, PillTone> = {
  active: 'success',
  inactive: 'warning',
  gone: 'danger',
};

export const ACCOUNT_STATUS_TONE: Record<ProfileStatus, PillTone> = {
  active: 'success',
  pending: 'warning',
  inactive: 'warning',
  suspended: 'danger',
  removed: 'danger',
  deleted: 'danger',
};

/** Lifetime order-count segments (FE plan Q9 — not the analytics repeat rate). */
export type EaterSegment = 'repeat' | 'single' | 'never';
export const EATER_SEGMENTS: EaterSegment[] = ['repeat', 'single', 'never'];
export const SEGMENT_LABEL: Record<EaterSegment, string> = {
  repeat: 'Repeat',
  single: 'Single order',
  never: 'Never ordered',
};
export const SEGMENT_TONE: Record<EaterSegment, PillTone> = { repeat: 'success', single: 'neutral', never: 'warning' };
export const SEGMENT_RANGE: Record<EaterSegment, { orderCountMin?: number; orderCountMax?: number }> = {
  repeat: { orderCountMin: 2 },
  single: { orderCountMin: 1, orderCountMax: 1 },
  never: { orderCountMax: 0 },
};
export function segmentOf(orderCount: number): EaterSegment {
  return orderCount >= 2 ? 'repeat' : orderCount === 1 ? 'single' : 'never';
}

/** BE `DishStatus`. `expired` reads as "Closed" to operators. */
const DISH_STATUS: Record<string, { label: string; tone: PillTone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  scheduled: { label: 'Scheduled', tone: 'info' },
  active: { label: 'Active', tone: 'success' },
  sold_out: { label: 'Sold out', tone: 'warning' },
  expired: { label: 'Closed', tone: 'neutral' },
  canceled: { label: 'Canceled', tone: 'danger' },
};
export function dishStatus(status: string): { label: string; tone: PillTone } {
  return DISH_STATUS[status] ?? { label: humanize(status), tone: 'neutral' };
}

const ORDER_STATUS_TONE: Record<string, PillTone> = {
  completed: 'success',
  canceled: 'danger',
  abandoned: 'neutral',
  no_show: 'danger',
  expired: 'neutral',
};
export function orderStatusTone(status: string): PillTone {
  return ORDER_STATUS_TONE[status] ?? 'info';
}
/** `ready_to_pickup` → "Ready to pickup". */
export function humanize(value: string): string {
  const s = value.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "Today", "1d ago", "12d ago" (fits a dialog stat tile); "—" when never. */
export function daysAgo(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return '—';
  const days = Math.floor((now.getTime() - Date.parse(value)) / 86_400_000);
  if (Number.isNaN(days)) return '—';
  if (days <= 0) return 'Today';
  return `${days}d ago`;
}

// --- URL param readers ----------------------------------------------------------

export function pickParam<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : undefined;
}

export function stringParam(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

export function listParam(value: unknown): string[] {
  return typeof value === 'string' && value ? value.split(',').filter(Boolean) : [];
}

export function numberParam(value: unknown, min: number, max: number): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value ? Number(value) : NaN;
  return Number.isFinite(n) && n >= min && n <= max ? n : undefined;
}

export function dayRangeParam(start: unknown, end: unknown): DayRange {
  return { start: isDay(start) ? start : undefined, end: isDay(end) ? end : undefined };
}

// --- cells & headers --------------------------------------------------------------

/** Table name cell: avatar and name. The profile ID lives in the dialog. */
export function ProfileCell({ name, avatarUrl }: { name: string; avatarUrl?: string }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2.5">
      <Avatar src={avatarUrl} name={name} size="md" />
      <span className="min-w-0 truncate font-semibold">{name || 'Unnamed'}</span>
    </span>
  );
}

/** "—" when never reviewed — or when the field is absent (a BE that predates it). */
export function RatingCell({ rating }: { rating: number | null | undefined }) {
  if (rating == null) return <span className="text-muted">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 tabular-nums">
      <Star size={13} aria-hidden="true" fill="currentColor" className="text-[#B49E31]" />
      {rating.toFixed(1)}
    </span>
  );
}

/** Kit dialog header: xl avatar with ring, name, a detail line, pills on the right. */
export function ProfileHeader({
  name,
  avatarUrl,
  detail,
  pills,
}: {
  name: string;
  avatarUrl?: string;
  detail: ReactNode;
  pills?: ReactNode;
}) {
  // On a phone the pills take their own line below, so the name has the width
  // up to the close button (and all of it in landscape).
  return (
    <div className="mb-[18px] flex flex-wrap items-center gap-x-4 gap-y-3 pr-8 desk:flex-nowrap">
      <Avatar src={avatarUrl} name={name} size="xl" ring />
      <div className="min-w-0 flex-1">
        <div className="truncate font-heading text-2xl font-bold tracking-[-.02em] text-brown-deep">
          {name || 'Unnamed'}
        </div>
        <div className="mt-[3px] text-[13px] text-muted">{detail}</div>
      </div>
      {pills && <div className="flex w-full flex-wrap gap-2 desk:w-auto desk:shrink-0 desk:justify-end">{pills}</div>}
    </div>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="mb-[9px] text-[11px] font-bold uppercase tracking-[.14em] text-muted">{children}</div>;
}
